import { computed } from "vue";
import { useStorage } from "@vueuse/core";
import { useSites, type Site } from "./listSites";

// Persist the selected site across page refreshes. HeaderSiteDropdown revalidates
// this on load and falls back to the first option if the stored site is gone.
const currentSiteId = useStorage<string | null>("camc.current-site-id", null);

export const useCurrentSite = () => {
  return { id: currentSiteId };
};

/**
 * A flag of the selected site. `allSites` is what it reads as when "All
 * sites" is selected: true for features that aggregate over every site (the
 * nav shows them), false for ones that only make sense one site at a time.
 */
const useSiteFlag = (flag: (site: Site) => boolean, allSites: boolean) => {
  const { data: sites } = useSites();
  const { id: currentSiteId } = useCurrentSite();

  return computed(() => {
    if (currentSiteId.value == null) return allSites;
    const site = sites.value?.find((site) => site.id === currentSiteId.value);
    return site ? flag(site) : false;
  });
};

export const useIsExperienceFreedom = () =>
  useSiteFlag((site) => site.isExperienceFreedom, true);
export const useHasExitCamera = () =>
  useSiteFlag((site) => site.hasExitCamera, true);
export const useIsDigitalArrivals = () =>
  useSiteFlag((site) => site.isDigitalArrivals, true);
export const useIsKeypad = () => useSiteFlag((site) => site.isKeypad, true);
export const useIsCompanion = () =>
  useSiteFlag((site) => site.isCompanion, true);

// False for "All sites": these are edited or viewed one site at a time. Use
// useIsCompanion for the cross-site Companion reads, which do aggregate.
export const useIsCompanionSiteSelected = () =>
  useSiteFlag((site) => site.isCompanion, false);
export const useHasKeysafePitch = () =>
  useSiteFlag((site) => site.hasKeysafePitch, false);
export const useHasBulkMessage = () =>
  useSiteFlag((site) => site.bulkMessageEnabled, false);
export const useHasStorageCompound = () =>
  useSiteFlag((site) => site.hasStorageCompound, false);

export function useSiteMapImage() {
  const { data: sites } = useSites();
  const { id: currentSiteId } = useCurrentSite();

  return computed(() => {
    if (currentSiteId.value == null) return null;
    const site = sites.value?.find((site) => site.id === currentSiteId.value);
    if (!site) return null;
    return site.siteMapImage ?? null;
  });
}
